import React, { useEffect, useState } from "react";
import { Filter, X, ChevronDown, ChevronUp, Search, Tag, Grid, Layers, Home, Star, CreditCard } from "lucide-react";
import { ICategory, ICategoryResponse } from "@/interfaces/Category/category.interface";
import { ITag, ITagResponse } from "@/interfaces/Tag/tag.interface";
import { callApi } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { CommonSelect } from "@/components/common/CommonSelect";
import { MultiSelect } from "@/components/common/MultiSelect";
import { IFilter } from "@/interfaces/common.interface";
import { usePermissions } from "@/hooks/usePermissions";

interface ProductAdvancedFiltersProps {
    filters: IFilter;
    updateFilter: (newFilters: Partial<IFilter>) => void;
    resetFilters: () => void;
}

export const ProductAdvancedFilters: React.FC<ProductAdvancedFiltersProps> = ({
    filters,
    updateFilter,
    resetFilters,
}) => {
    const { isAdmin } = usePermissions();

    const [isExpanded, setIsExpanded] = useState(false);
    const [categories, setCategories] = useState<ICategory[]>([]);
    const [tags, setTags] = useState<ITag[]>([]);
    const [stores, setStores] = useState<any[]>([]);
    const [loading, setLoading] = useState({
        categories: false,
        tags: false,
        stores: false
    });

    const fetchCategories = async (search = "") => {
        setLoading(prev => ({ ...prev, categories: true }));
        try {
            const response = await callApi<ICategoryResponse>(`${API_URL}${API_ENDPOINT.categories}?limit=100${search ? `&search=${search}` : ""}`, { method: 'GET' });
            setCategories(response?.data?.categories || []);
        } finally {
            setLoading(prev => ({ ...prev, categories: false }));
        }
    };

    const fetchTags = async (search = "") => {
        setLoading(prev => ({ ...prev, tags: true }));
        try {
            const response = await callApi<ITagResponse>(`${API_URL}${API_ENDPOINT.tags}?limit=100${search ? `&search=${search}` : ""}`, { method: 'GET' });
            setTags(response?.data?.tags || []);
        } finally {
            setLoading(prev => ({ ...prev, tags: false }));
        }
    };

    const fetchStores = async (search = "") => {
        if (!isAdmin) return;
        setLoading(prev => ({ ...prev, stores: true }));
        try {
            const response = await callApi<any>(`${API_URL}${API_ENDPOINT.stores}/all?limit=100${search ? `&search=${search}` : ""}`, { method: 'GET' });
            setStores(response?.data?.stores || response?.data || []);
        } finally {
            setLoading(prev => ({ ...prev, stores: false }));
        }
    };

    const [localPrices, setLocalPrices] = useState({
        min: filters.minPrice || "",
        max: filters.maxPrice || ""
    });

    // Debounce price updates
    useEffect(() => {
        const timer = setTimeout(() => {
            if (localPrices.min !== (filters.minPrice || "") || localPrices.max !== (filters.maxPrice || "")) {
                updateFilter({
                    minPrice: localPrices.min,
                    maxPrice: localPrices.max
                });
            }
        }, 600); // 600ms debounce for prices

        return () => clearTimeout(timer);
    }, [localPrices, updateFilter, filters.minPrice, filters.maxPrice]);

    // Update local state when filters are reset
    useEffect(() => {
        setLocalPrices({
            min: filters.minPrice || "",
            max: filters.maxPrice || ""
        });
    }, [filters.minPrice, filters.maxPrice]);

    const fetchData = async () => {
        await Promise.all([
            fetchCategories(),
            fetchTags(),
            fetchStores()
        ]);
    };

    useEffect(() => {
        if (isExpanded) {
            fetchData();
        }
    }, [isExpanded]);

    const [activeCategory, setActiveCategory] = useState<ICategory | null>(null);

    useEffect(() => {
        if (filters.categoryIds) {
            const found = categories.find(c => c.id === filters.categoryIds);
            if (found) {
                setActiveCategory(found);
            } else if (!activeCategory || activeCategory.id !== filters.categoryIds) {
                // If not found in current search results, fetch it specifically
                callApi<any>(`${API_URL}${API_ENDPOINT.categories}/${filters.categoryIds}`, { method: 'GET' })
                    .then(res => setActiveCategory(res.data))
                    .catch(err => console.error("Failed to fetch active category details", err));
            }
        } else {
            setActiveCategory(null);
        }
    }, [filters.categoryIds, categories]);

    const subCategoryOptions = activeCategory?.subcategories?.map(s => ({
        label: s.name,
        value: s.id
    })) || [];

    const hasActiveFilters = Object.keys(filters).some(key => {
        if (['page', 'limit', 'sort', 'search', 'userId', 'isGlobal'].includes(key)) return false;
        const val = filters[key as keyof IFilter];
        if (Array.isArray(val)) return val.length > 0;
        return !!val;
    });

    return (
        <div className="bg-card rounded-2xl border border-secondary/20 overflow-hidden transition-all duration-300 shadow-sm">
            {/* Header / Basic Search */}
            <div className="p-4 flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[280px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search products by name or ID..."
                        className="w-full pl-10 pr-4 py-2 bg-secondary/5 border border-secondary/15 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
                        value={filters.search || ""}
                        onChange={(e) => updateFilter({ search: e.target.value })}
                    />
                </div>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setIsExpanded(!isExpanded)}
                        className={`
                            flex items-center gap-2.5 px-4 h-10 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer
                            ${isExpanded
                                ? 'bg-primary text-white shadow-lg shadow-primary/25 border-transparent'
                                : 'bg-secondary/10 text-foreground border border-secondary/20 hover:bg-secondary/20 hover:border-secondary/30'
                            }
                        `}
                    >
                        <Filter className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-180 scale-110' : ''}`} />
                        <span>Advanced Filters</span>
                        <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                        </div>
                    </button>

                    {hasActiveFilters && (
                        <button
                            type="button"
                            onClick={resetFilters}
                            className="flex items-center gap-1.5 px-4 h-10 rounded-xl text-sm font-semibold text-destructive hover:bg-destructive/10 transition-all duration-200 cursor-pointer border border-transparent hover:border-destructive/10 active:scale-95"
                        >
                            <X className="w-4 h-4" />
                            <span>Clear All</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded Filters */}
            {isExpanded && (
                <div className="p-5 border-t border-secondary/15 bg-secondary/5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 animate-in slide-in-from-top-2 duration-300">

                    {/* Categories */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Grid className="w-3 h-3" /> Category
                        </label>
                        <MultiSelect
                            options={categories.map(c => ({ label: c.name, value: c.id }))}
                            selected={filters.categoryIds ? [filters.categoryIds] : []}
                            onChange={(vals) => updateFilter({ categoryIds: vals[0] || "", subCategoryId: "" })}
                            onSearchChange={fetchCategories}
                            placeholder="Select Categories"
                            singleSelect
                            loading={loading.categories}
                        />
                    </div>

                    {/* Sub Category */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Layers className="w-3 h-3" /> Sub Category
                        </label>
                        <MultiSelect
                            options={subCategoryOptions}
                            selected={filters.subCategoryId ? [filters.subCategoryId] : []}
                            onChange={(vals) => updateFilter({ subCategoryId: vals[0] || "" })}
                            placeholder={filters.categoryIds ? "Select Sub Categories" : "Select Category first"}
                            disabled={!filters.categoryIds}
                            singleSelect
                        />
                    </div>

                    {/* Tags */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Tag className="w-3 h-3" /> Tag
                        </label>
                        <MultiSelect
                            options={tags.map(t => ({ label: t.name, value: t.id }))}
                            selected={filters.tagId ? [filters.tagId] : []}
                            onChange={(vals) => updateFilter({ tagId: vals[0] || "" })}
                            onSearchChange={fetchTags}
                            placeholder="Select Tags"
                            singleSelect
                            loading={loading.tags}
                        />
                    </div>

                    {/* Store - Only for Admin */}
                    {isAdmin && (
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                                <Home className="w-3 h-3" /> Store
                            </label>
                            <MultiSelect
                                options={stores.map(storeData => ({ label: storeData.name || storeData.fullName || "Unnamed Store", value: storeData.id }))}
                                selected={filters.storeId ? [filters.storeId] : []}
                                onChange={(vals) => updateFilter({ storeId: vals[0] || "" })}
                                onSearchChange={fetchStores}
                                placeholder="Select Stores"
                                singleSelect
                                loading={loading.stores}
                            />
                        </div>
                    )}

                    {/* Price Range */}
                    <div className="space-y-1.5 lg:col-span-2">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <CreditCard className="w-3 h-3" /> Price Range
                        </label>
                        <div className="flex items-center gap-3">
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">R</span>
                                <input
                                    type="number"
                                    placeholder="Min"
                                    min="0"
                                    className="w-full pl-7 pr-3 py-2 bg-card border border-secondary/20 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
                                    value={localPrices.min}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val !== "" && Number(val) < 0) return;
                                        setLocalPrices(prev => ({ ...prev, min: val }));
                                    }}
                                    onWheel={(e) => e.currentTarget.blur()}
                                />
                            </div>
                            <span className="text-muted-foreground">—</span>
                            <div className="relative flex-1">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs font-bold text-muted-foreground">R</span>
                                <input
                                    type="number"
                                    placeholder="Max"
                                    min="0"
                                    className="w-full pl-7 pr-3 py-2 bg-card border border-secondary/20 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-primary/30"
                                    value={localPrices.max}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val !== "" && Number(val) < 0) return;
                                        setLocalPrices(prev => ({ ...prev, max: val }));
                                    }}
                                    onWheel={(e) => e.currentTarget.blur()}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Rating */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <Star className="w-3 h-3" /> Min Rating
                        </label>
                        <CommonSelect
                            value={filters.rating}
                            onValueChange={(val) => updateFilter({ rating: val })}
                            options={[
                                { label: "Any Rating", value: "" },
                                { label: "4.0+ Stars", value: "4" },
                                { label: "3.0+ Stars", value: "3" },
                                { label: "2.0+ Stars", value: "2" },
                                { label: "1.0+ Stars", value: "1" },
                            ]}
                            placeholder="Select Rating"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20"
                        />
                    </div>

                    {/* Status */}
                    <div className="space-y-1.5">
                        <label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 px-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${filters.status === 'active' ? 'bg-green-500' : filters.status === 'inactive' ? 'bg-red-500' : 'bg-gray-400'}`} /> Status
                        </label>
                        <CommonSelect
                            value={filters.status}
                            onValueChange={(val) => updateFilter({ status: val })}
                            options={[
                                { label: "All Status", value: "" },
                                { label: "Active", value: "active" },
                                { label: "Inactive", value: "inactive" },
                            ]}
                            placeholder="Select Status"
                            triggerClassName="w-full h-10 rounded-xl bg-card border-secondary/20"
                        />
                    </div>

                </div>
            )}
        </div>
    );
};
